import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function ProfitMarginCalculator() {
  const [revenue, setRevenue] = useState("");
  const [costOfGoods, setCostOfGoods] = useState("");
  const [operatingExpenses, setOperatingExpenses] = useState("");
  const [result, setResult] = useState<{ gross: number; net: number } | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Finance", url: "https://calclab.app/finance" },
      { name: "Profit Margin Calculator", url: "https://calclab.app/calculator/profit-margin" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Profit Margin Calculator",
      "Calculate gross and net profit margins for financial planning. Free online finance calculator with instant results and comprehensive analysis.",
      "https://calclab.app/calculator/profit-margin",
      lang
    )
  ]);

  const calculate = () => {
    const R = parseFloat(revenue);
    const COGS = parseFloat(costOfGoods);
    const OE = parseFloat(operatingExpenses);

    if (isNaN(R) || isNaN(COGS) || isNaN(OE) || R <= 0 || COGS < 0 || OE < 0) {
      toast.error("Please enter valid values");
      return;
    }

    const grossProfit = R - COGS;
    const grossMargin = (grossProfit / R) * 100;
    
    const netProfit = grossProfit - OE;
    const netMargin = (netProfit / R) * 100;

    setResult({ gross: grossMargin, net: netMargin });
    toast.success("Profit margins calculated!");
  };

  const reset = () => {
    setRevenue("");
    setCostOfGoods("");
    setOperatingExpenses("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Profit Margin Calculator</CardTitle>
          <CardDescription>
            Calculate gross and net profit margins
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="revenue">Revenue ($)</Label>
            <Input
              id="revenue"
              type="number"
              placeholder="100000"
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="costOfGoods">Cost of Goods Sold ($)</Label>
            <Input
              id="costOfGoods"
              type="number"
              placeholder="60000"
              value={costOfGoods}
              onChange={(e) => setCostOfGoods(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="operatingExpenses">Operating Expenses ($)</Label>
            <Input
              id="operatingExpenses"
              type="number"
              placeholder="20000"
              value={operatingExpenses}
              onChange={(e) => setOperatingExpenses(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Gross Margin:</strong> {result.gross.toFixed(2)}%
                </p>
                <CopyResultButton value={result.gross.toFixed(2)} />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Net Margin:</strong> {result.net.toFixed(2)}%
                </p>
                <CopyResultButton value={result.net.toFixed(2)} />
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["profit-margin"]} trigger={result?.net} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
